import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * Searchable employee picker (shadcn Command + Popover).
 * Filters users by name/role as you type. Same data source as the Select it replaces.
 */
export function EmployeeCombobox({
  users = [],
  value,
  onChange,
  placeholder = 'Select employee...',
  searchPlaceholder = 'Search employee...',
  emptyText = 'No employee found.',
  testId = 'employee-combobox',
}) {
  const [open, setOpen] = useState(false);
  const fullName = (u) => `${u.first_name || ''} ${u.last_name || ''}`.trim();
  const selected = users.find((u) => u.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="w-full h-11 justify-between font-normal"
        >
          {selected ? (
            <span className="flex items-center gap-2 min-w-0">
              <span className="w-6 h-6 rounded-full bg-[#003366] text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                {selected.first_name?.[0]}{selected.last_name?.[0]}
              </span>
              <span className="truncate">{fullName(selected)}</span>
              <span className="text-xs text-gray-400 flex-shrink-0">({selected.role})</span>
            </span>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} data-testid={`${testId}-input`} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {users.map((u) => (
                <CommandItem
                  key={u.id}
                  value={`${fullName(u)} ${u.role || ''} ${u.id}`}
                  data-testid={`${testId}-option-${u.id}`}
                  onSelect={() => { onChange(u.id); setOpen(false); }}
                >
                  <Check className={cn('mr-2 h-4 w-4', value === u.id ? 'opacity-100' : 'opacity-0')} />
                  <span className="w-6 h-6 rounded-full bg-[#003366] text-white flex items-center justify-center text-[10px] font-bold mr-2">
                    {u.first_name?.[0]}{u.last_name?.[0]}
                  </span>
                  <span>{fullName(u)}</span>
                  <span className="ml-2 text-xs text-gray-400">({u.role})</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
